import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Pencil } from "lucide-react";
import PageShell from "../dashboard/PageShell";

const readOnlyCell = (label: string, value?: React.ReactNode) => (
  <tr>
    <td className="px-4 py-2 text-sm text-gray-700 w-64 align-top bg-gray-50 border-b border-gray-200">
      {label}
    </td>
    <td className="px-4 py-2 text-sm text-gray-900 border-b border-gray-200">
      {value ?? <span className="text-gray-400">-</span>}
    </td>
  </tr>
);

const ContractDetail: React.FC = () => {
  const navigate = useNavigate();
  const id = useMemo(() => {
    try {
      const parts = window.location.pathname.split("/").filter(Boolean);
      return parts[parts.length - 1] || "";
    } catch {
      return "";
    }
  }, []);

  const [activeTab, setActiveTab] = useState(() => {
    // Load active tab from localStorage, default to "contract-info"
    return localStorage.getItem("contractDetailActiveTab") || "contract-info";
  });

  const data = useMemo(
    () => ({
      contractName: "HĐ 1",
      contractCode: "",
      contractValue: "90.000.000 ₫",
      signingDate: "2021",
      signerName: "- Chọn -",
      phone: "",
      position: "",
      idNumber: "",
      managingUnit: "Phòng Kỹ thuật nhiệt đới",
      partner: "",
      notes: "",
    }),
    []
  );

  const handleTabChange = (tabKey: string) => {
    setActiveTab(tabKey);
    localStorage.setItem("contractDetailActiveTab", tabKey);
  };

  return (
    <PageShell title="Chi tiết hợp đồng">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => window.history.back()}
              aria-label="Go back"
              className="p-3 rounded-full text-[#337ab7] bg-blue-50 hover:bg-blue-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-lg font-semibold text-gray-800 truncate">
              Chi tiết hợp đồng
              <span className="ml-2 text-sm font-normal text-gray-600 truncate align-middle">
                – {data.contractName}
              </span>
            </h1>
          </div>
          <button
            onClick={() => navigate(`/contracts/edit/${id}`)}
            className="bg-[#337ab7] text-white px-4 py-2 rounded text-sm font-medium hover:bg-[#2e6da4] transition-colors flex items-center space-x-2 justify-center min-w-[110px]"
          >
            <Pencil className="h-4 w-4" />
            <span>Sửa</span>
          </button>
        </div>

        <div className="bg-white rounded-lg">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6 overflow-x-auto">
              {[
                { key: "contract-info", label: "Thông tin hợp đồng" },
                { key: "full-content", label: "Nội dung đầy đủ" },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => handleTabChange(t.key)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === t.key
                      ? "border-[#337ab7] text-[#337ab7]"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === "contract-info" && (
              <div className="space-y-6">
                <table className="w-full border border-gray-200 rounded-md overflow-hidden">
                  <tbody>
                    {readOnlyCell("Tên hợp đồng", data.contractName)}
                    {readOnlyCell("Mã hợp đồng", data.contractCode)}
                    {readOnlyCell("Giá trị hợp đồng", data.contractValue)}
                    {readOnlyCell("Ngày ký hợp đồng", data.signingDate)}
                    {readOnlyCell(
                      "Tên cá nhân đứng tên hợp đồng",
                      data.signerName
                    )}
                    {readOnlyCell("Điện thoại", data.phone)}
                    {readOnlyCell("Chức vụ", data.position)}
                    {readOnlyCell("Số CMT", data.idNumber)}
                    {readOnlyCell(
                      "Đơn vị/ Phòng ban quản lý",
                      data.managingUnit
                    )}
                    {readOnlyCell("Đối tác", data.partner)}
                    {readOnlyCell("Ghi chú", data.notes)}
                  </tbody>
                </table>
              </div>
            )}

            {activeTab === "full-content" && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="text-sm text-gray-700 leading-relaxed space-y-3">
                  <p>
                    <strong>Tên hợp đồng:</strong> {data.contractName}
                  </p>

                  <p>
                    <strong>Mã hợp đồng:</strong>{" "}
                    {data.contractCode || "Chưa có mã"}
                  </p>

                  <p>
                    <strong>Giá trị hợp đồng:</strong> {data.contractValue}
                  </p>

                  <p>
                    <strong>Ngày ký hợp đồng:</strong> {data.signingDate}
                  </p>

                  <p>
                    <strong>Tên cá nhân đứng tên hợp đồng:</strong>{" "}
                    {data.signerName}
                  </p>

                  <p>
                    <strong>Điện thoại:</strong> {data.phone || "Chưa có"}
                  </p>

                  <p>
                    <strong>Chức vụ:</strong> {data.position || "Chưa có"}
                  </p>

                  <p>
                    <strong>Số CMT:</strong> {data.idNumber || "Chưa có"}
                  </p>

                  <p>
                    <strong>Đơn vị/ Phòng ban quản lý:</strong>{" "}
                    {data.managingUnit}
                  </p>

                  <p>
                    <strong>Đối tác:</strong> {data.partner || "Chưa có"}
                  </p>

                  <p>
                    <strong>Ghi chú:</strong> {data.notes || "Không có ghi chú"}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default ContractDetail;
